export type WorkspaceCampaignPlacementMode = 'SET' | 'INCREASE' | 'DECREASE';

export type WorkspaceCampaignPlacementKey = 'TOP_OF_SEARCH' | 'REST_OF_SEARCH' | 'PRODUCT_PAGES';

export type WorkspaceCampaignPlacementJobStatus =
  | 'pending'
  | 'running'
  | 'completed'
  | 'completed_with_errors'
  | 'failed';

export type WorkspaceCampaignPlacementResultStatus = 'pending' | 'applied' | 'skipped' | 'failed';

export const CAMPAIGN_PLACEMENT_FIELDS: Array<{
  id: WorkspaceCampaignPlacementKey;
  label: string;
}> = [
  { id: 'TOP_OF_SEARCH', label: 'Top of Search' },
  { id: 'REST_OF_SEARCH', label: 'Rest of Search' },
  { id: 'PRODUCT_PAGES', label: 'Product Pages' },
];

export const CAMPAIGN_PLACEMENT_MODE_LABELS: Record<WorkspaceCampaignPlacementMode, string> = {
  INCREASE: 'Increase by',
  DECREASE: 'Decrease by',
  SET: 'Set to',
};

export type CampaignPlacementAdjustment = {
  placement: WorkspaceCampaignPlacementKey;
  mode: WorkspaceCampaignPlacementMode;
  value: number;
};

export type CampaignPlacementRowState = {
  mode: WorkspaceCampaignPlacementMode | '';
  value: string;
};

export type CampaignPlacementFormState = Record<
  WorkspaceCampaignPlacementKey,
  CampaignPlacementRowState
>;

export function emptyCampaignPlacementForm(): CampaignPlacementFormState {
  return {
    TOP_OF_SEARCH: { mode: '', value: '0' },
    REST_OF_SEARCH: { mode: '', value: '0' },
    PRODUCT_PAGES: { mode: '', value: '0' },
  };
}

export function parseCampaignPlacementAdjustments(
  form: CampaignPlacementFormState
): { adjustments: CampaignPlacementAdjustment[]; error: string | null } {
  const adjustments: CampaignPlacementAdjustment[] = [];
  for (const field of CAMPAIGN_PLACEMENT_FIELDS) {
    const row = form[field.id];
    const raw = row.value.trim();
    if (!row.mode && (raw === '' || raw === '0')) continue;
    if (!row.mode) {
      return { adjustments: [], error: `Choose how to adjust ${field.label}` };
    }
    if (raw === '') {
      return { adjustments: [], error: `Enter a value for ${field.label}` };
    }
    const numeric = Number(raw);
    if (!Number.isFinite(numeric)) {
      return { adjustments: [], error: `Enter a valid number for ${field.label}` };
    }
    if ((row.mode === 'INCREASE' || row.mode === 'DECREASE') && numeric <= 0) {
      return { adjustments: [], error: `${field.label} change must be greater than 0` };
    }
    adjustments.push({ placement: field.id, mode: row.mode, value: numeric });
  }
  if (adjustments.length === 0) {
    return { adjustments: [], error: 'Adjust at least one placement' };
  }
  return { adjustments, error: null };
}

export type WorkspaceCampaignPlacementJob = {
  id: string;
  profileId: string;
  status: WorkspaceCampaignPlacementJobStatus;
  adjustments: CampaignPlacementAdjustment[];
  selection: Record<string, unknown>;
  totals: {
    total?: number;
    applied?: number;
    failed?: number;
    skipped?: number;
    pending?: number;
  };
  applyProgress: {
    processed?: number;
    total?: number;
    error?: string;
  };
  startedAt: string | null;
  finishedAt: string | null;
  createdByUserId: string | null;
  createdAt: string | null;
};

export type WorkspaceCampaignPlacementResult = {
  id: string;
  jobId: string;
  campaignId: string;
  adProduct: string;
  campaignName: string | null;
  previousPlacements: Record<string, number>;
  proposedPlacements: Record<string, number>;
  status: WorkspaceCampaignPlacementResultStatus;
  errorCode: string | null;
  errorMessage: string | null;
  appliedAt: string | null;
  retryCount: number;
};

export type WorkspaceCampaignPlacementJobCreateBody = {
  adjustments: CampaignPlacementAdjustment[];
  campaigns: Array<{ campaignId: string; adProduct: 'SP' | 'SB' | 'SD' }>;
};

export const WORKSPACE_CAMPAIGN_PLACEMENT_TERMINAL_STATUSES =
  new Set<WorkspaceCampaignPlacementJobStatus>(['completed', 'completed_with_errors', 'failed']);

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function asNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function asString(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function asNumberMap(value: unknown): Record<string, number> {
  const rec = asRecord(value);
  const out: Record<string, number> = {};
  for (const [key, item] of Object.entries(rec)) {
    const n = asNumber(item);
    if (n != null) out[key] = n;
  }
  return out;
}

export function parseWorkspaceCampaignPlacementJob(
  data: unknown
): WorkspaceCampaignPlacementJob | null {
  const raw = asRecord(data);
  const id = asString(raw.id);
  const profileId = asString(raw.profileId) ?? asString(raw.profile_id);
  const status = asString(raw.status) as WorkspaceCampaignPlacementJobStatus | null;
  if (!id || !profileId || !status) return null;
  const totals = asRecord(raw.totals);
  const progress = asRecord(raw.applyProgress ?? raw.apply_progress);
  return {
    id,
    profileId,
    status,
    adjustments: Array.isArray(raw.adjustments)
      ? (raw.adjustments as CampaignPlacementAdjustment[])
      : [],
    selection: asRecord(raw.selection),
    totals: {
      total: asNumber(totals.total) ?? undefined,
      applied: asNumber(totals.applied) ?? undefined,
      failed: asNumber(totals.failed) ?? undefined,
      skipped: asNumber(totals.skipped) ?? undefined,
      pending: asNumber(totals.pending) ?? undefined,
    },
    applyProgress: {
      processed: asNumber(progress.processed) ?? undefined,
      total: asNumber(progress.total) ?? undefined,
      error: asString(progress.error) ?? undefined,
    },
    startedAt: asString(raw.startedAt) ?? asString(raw.started_at),
    finishedAt: asString(raw.finishedAt) ?? asString(raw.finished_at),
    createdByUserId: asString(raw.createdByUserId) ?? asString(raw.created_by_user_id),
    createdAt: asString(raw.createdAt) ?? asString(raw.created_at),
  };
}

export function parseWorkspaceCampaignPlacementResult(
  data: unknown
): WorkspaceCampaignPlacementResult | null {
  const raw = asRecord(data);
  const id = asString(raw.id);
  const campaignId = asString(raw.campaignId) ?? asString(raw.campaign_id);
  const status = asString(raw.status) as WorkspaceCampaignPlacementResultStatus | null;
  if (!id || !campaignId || !status) return null;
  return {
    id,
    jobId: asString(raw.jobId) ?? asString(raw.job_id) ?? '',
    campaignId,
    adProduct: asString(raw.adProduct) ?? asString(raw.ad_product) ?? 'SP',
    campaignName: asString(raw.campaignName) ?? asString(raw.campaign_name),
    previousPlacements: asNumberMap(raw.previousPlacements ?? raw.previous_placements),
    proposedPlacements: asNumberMap(raw.proposedPlacements ?? raw.proposed_placements),
    status,
    errorCode: asString(raw.errorCode) ?? asString(raw.error_code),
    errorMessage: asString(raw.errorMessage) ?? asString(raw.error_message),
    appliedAt: asString(raw.appliedAt) ?? asString(raw.applied_at),
    retryCount: asNumber(raw.retryCount) ?? asNumber(raw.retry_count) ?? 0,
  };
}
